/**
 * Einziger Zugang zur API.
 *
 * Keine Komponente ruft fetch selbst auf. Alle Pfade sind relativ -- damit
 * gilt dieselbe Herkunft wie im Betrieb und es entsteht kein CORS-Sonderfall.
 *
 * Hier findet KEINE fachliche Berechnung statt (HOMEBASE_ARCHITECTURE.md 3.1).
 */
import type {
  ArticleDetail, ArticleListQuery, ArticleListResponse, ArticleTextsResponse, TextBasis,
  DeletePreflight, PurgeArticleRequest, TrashArticleRequest, TrashListResponse,
  ArticleVariantSpaceResponse, ChangePasswordRequest,
  CreateLocaleRequest, CreateTextRequest, CreateUserRequest, FamilyTextsResponse, MetaResponse,
  ArticlePropertiesResponse, CreatePropertyRequest, FamilyPropertiesResponse, ProductText,
  SessionResponse, UpdateLocaleRequest, UpdatePropertyRequest, UpdateUserRequest,
  UserListItem, UserListResponse,
  CreateDimensionRequest, CreateDimensionValueRequest, CreateModelRequest, CreateVariantRequest,
  ModelPropertiesResponse, ProductModelDetail, ProductModelListResponse,
  PropertyReviewQuery, PropertyReviewResponse,
  CleanupFamilyResponse, CleanupModelView, OpenReviewFlagRequest, ResolveReviewFlagRequest,
  ArticleCapability, ProvenCapabilityCatalog,
  ArticleDocumentsResponse, ModelDocumentsResponse, UploadDocumentRequest,
  SetDteListingRequest, UpdateModelRequest, UpdateVariantRequest,
  AdoptCommonValueRequest, ModelCommonDataResponse, ModelHistoryResponse, ModelOverviewResponse,
  AssignArticleRequest, AssignmentCandidatesResponse, AssignmentCheck, ModelDeletePreview,
  DiscardPreview, DiscardRequest, UnassignPreview,
  ArticleLogisticsResponse, CreatePackagingLevelRequest, CreatePackagingTestRequest,
  CreatePackagingVersionRequest, CreateSupplierArticleRequest, SetPackagingMaterialsRequest,
  ArticleCustomsResponse, CustomsFactCode, CustomsFactRequest, ForwarderReceiptRequest, ReleaseAssignmentRequest,
  CalculationScenariosResponse, ModelPackagingResponse, ModelReleaseScope, SetTariffCodeRequest, SupplierArticle, UpdatePackagingLevelRequest,
  UpdatePackagingVersionRequest, UpdateSupplierArticleRequest,
  ArticleCalculationResponse, ArticleDossier, ArticleRelationRef, BreakerCapacity,
  PaymentTerms, SetPaymentTermsRequest,
  SupplierListItem, SupplierDetail, SupplierArticleRow, SupplierContact,
  UpdateSupplierRequest, UpsertSupplierContactRequest, CreateSupplierRequest,
  SupplierNote, UpsertSupplierNoteRequest,
  SupplierBankAccount, UpsertSupplierBankAccountRequest,
  PurchaseOrderListItem, PurchaseOrderDetail, CreatePurchaseOrderRequest,
  PurchaseOrderCapture, CreateCaptureRequest, ConfirmCaptureRequest,
  PurchaseOrderFinance, UpsertPaymentRequest, CommitmentReport,
  UpdatePurchaseOrderRequest, UpsertPurchaseOrderLineRequest,
  ConsolidationListItem, ConsolidationDetail, ConsolidationGoods,
  ConsolidationDocumentsResponse, OrderDocumentsResponse, UploadOrderDocumentRequest,
  UploadPaymentEvidenceRequest,
  CreateConsolidationRequest,
  UpdateConsolidationRequest, UpsertMilestoneRequest, Milestone,
  ChinaEvent, ChinaEventsResponse, UpsertChinaEventRequest,
  LeadTimeRule, UpsertLeadTimeRuleRequest, TimelineResponse,
  SupplierRating, UpdateSupplierRatingRequest,
  CalculateRequest, CalculationParametersResponse, CreateArticleRequest, CreateCategoryRequest,
  CreateFamilyRequest, CategoryItem, StructureDeletePreview, StructureKind, UpdateStructureRequest,
  AdoptFromArticlePreview, AdoptFromArticleRequest, ArticleRequirements, ModelRequirements, DropOwnPreview, DropOwnValueRequest, RemoveCommonValueRequest,
  CreatePropertyDefinitionRequest, PropertyDefinitionUsage, UpdatePropertyDefinitionRequest,
  FamilyItem,
  InformationRequestRef, ReleasePriceRequest, SetCalculationParameterRequest,
  SetDutyRateRequest, SetSupplierPriceRequest, UpdateArticleRequest,
  SupplierRequirements,
} from "@homebase/shared";
import { API_BASE } from "@homebase/shared";

/**
 * Fehler an der API-Grenze.
 *
 * Traegt den stabilen Fehlercode. Die Meldung des Servers wird mitgefuehrt,
 * aber in der Oberflaeche NICHT angezeigt -- dort wird der Code auf einen
 * uebersetzten Text abgebildet (`i18n/errors.ts`). Andernfalls entstuende ein
 * zweiter, unuebersetzter Textbestand neben den Woerterbuechern.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Wartezeit aus dem `retry-after`-Kopf, wenn der Server begrenzt hat. */
    readonly retryAfterSec?: number,
    /**
     * Zahlen zum Fehler, vom Server gerechnet. Die Oberflaeche nennt sie in
     * ihrem eigenen uebersetzten Satz -- sie rechnet sie nicht nach.
     */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: "same-origin",
      headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError(0, "network", "network");
  }
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const retry = Number(res.headers.get("retry-after"));
    throw new ApiError(
      res.status,
      body?.error?.code ?? "unknown",
      body?.error?.message ?? "unknown",
      Number.isFinite(retry) && retry > 0 ? retry : undefined,
      body?.error?.details ?? undefined,
    );
  }
  return body as T;
}

function toQuery(q: ArticleListQuery): string {
  const p = new URLSearchParams();
  if (q.q) p.set("q", q.q);
  if (q.categoryId) p.set("categoryId", String(q.categoryId));
  if (q.familyId) p.set("familyId", String(q.familyId));
  if (q.roleCode) p.set("roleCode", q.roleCode);
  // 0 ist ein gueltiger Filterwert -- "ohne Produktmodell". Deshalb wird auf
  // undefined geprueft und nicht auf Wahrheit.
  if (q.modelId !== undefined) p.set("modelId", String(q.modelId));
  /* Der Betriebszustand -- ohne ihn faende der Server nur seine eigene
     Vorgabe „alle" vor, und die Auswahl der Oberflaeche bliebe wirkungslos. */
  if (q.availability) p.set("availability", q.availability);
  if (q.lifecycle) p.set("lifecycle", q.lifecycle);
  if (q.stocking) p.set("stocking", q.stocking);
  if (q.operational) p.set("operational", q.operational);
  if (q.sort) p.set("sort", q.sort);
  if (q.dir) p.set("dir", q.dir);
  if (q.page) p.set("page", String(q.page));
  if (q.pageSize) p.set("pageSize", String(q.pageSize));
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Die Filter der Pruefwarteschlange. Leere Filter erscheinen nicht im Pfad. */
function toReviewQuery(q: PropertyReviewQuery): string {
  const p = new URLSearchParams();
  if (q.q) p.set("q", q.q);
  if (q.findingClass) p.set("findingClass", q.findingClass);
  if (q.definition) p.set("definition", q.definition);
  if (q.quality) p.set("quality", q.quality);
  if (q.role) p.set("role", q.role);
  if (q.page) p.set("page", String(q.page));
  if (q.pageSize) p.set("pageSize", String(q.pageSize));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const api = {
  session: () => request<SessionResponse>("/session"),
  login: (username: string, password: string) =>
    request<SessionResponse>("/session", { method: "POST", body: JSON.stringify({ username, password }) }),
  logout: () => request<SessionResponse>("/session", { method: "DELETE" }),
  meta: () => request<MetaResponse>("/meta"),

  // Eigenes Passwort. Ohne Benutzernamen -- das Konto ergibt sich aus der
  // Sitzung. Verlangt eine Anmeldung, aber keine Berechtigung.
  changePassword: (input: ChangePasswordRequest) =>
    request<SessionResponse>("/password", { method: "POST", body: JSON.stringify(input) }),
  articles: (q: ArticleListQuery) => request<ArticleListResponse>(`/articles${toQuery(q)}`),

  article: (id: number) => request<ArticleDetail>(`/articles/${id}`),

  /* ── Produkt-Papierkorb ──────────────────────────────────────────────────
   *
   * Fuenf eigene Aufrufe statt eines Feldes im Artikel-PATCH. Ein
   * `patch({deleted: true})` waere kuerzer und liefe ueber die Berechtigung
   * zum Pflegen -- hier braucht die endgueltige Loeschung eine eigene.
   */
  trashArticle: (id: number, input: TrashArticleRequest) =>
    request<{ ok: true }>(`/articles/${id}/trash`,
      { method: "POST", body: JSON.stringify(input) }),
  restoreArticle: (id: number) =>
    request<{ ok: true }>(`/articles/${id}/restore`, { method: "POST" }),
  trash: () => request<TrashListResponse>("/articles/trash"),
  deletePreflight: (id: number) => request<DeletePreflight>(`/articles/${id}/delete-preflight`),
  purgeArticle: (id: number, input: PurgeArticleRequest) =>
    request<{ tombstoneId: number }>(`/articles/${id}/permanent`,
      { method: "DELETE", body: JSON.stringify(input) }),
  /**
   * Einen Aenderungsvorschlag entscheiden.
   *
   * NICHT dasselbe wie eine fachliche Bestaetigung: hier wird entschieden,
   * WELCHER Wert gilt -- dort, ob der geltende belastbar ist.
   */
  decideProposal: (id: number, decision: "angenommen" | "abgelehnt", note?: string) =>
    request<{ proposalId: number; decision: string }>(`/proposals/${id}/decision`,
      { method: "POST", body: JSON.stringify({ decision, note }) }),

  /** Der Variantenraum eines Artikels: welche Ausfuehrungen es gibt. */
  variantSpace: (id: number) =>
    request<ArticleVariantSpaceResponse>(`/articles/${id}/variant-space`),

  // Benutzerverwaltung. Erreichbar nur mit der Faehigkeit user.manage -- das
  // entscheidet der Server, nicht diese Datei.
  // Redaktionelle Produkttexte. Lesen mit article.read, schreiben mit
  // text.write, freigeben mit text.release -- geprueft wird im Server.
  articleTexts: (articleId: number) => request<ArticleTextsResponse>(`/articles/${articleId}/texts`),
  /** Die Textgrundlage einer SKU -- die Schnittstelle, an der eine
      Texterzeugung spaeter andockt. Erzeugt heute nichts. */
  textBasis: (articleId: number) =>
    request<{ basis: TextBasis; fingerprint: string }>(`/articles/${articleId}/text-basis`),
  familyTexts: (familyId: number) => request<FamilyTextsResponse>(`/families/${familyId}/texts`),

  // Strukturierte Produktmerkmale. Artikelwerte sind ausschliesslich
  // Artikelwerte -- der Server loest nichts ueber die Familie auf.
  articleProperties: (articleId: number) =>
    request<ArticlePropertiesResponse>(`/articles/${articleId}/properties`),
  familyProperties: (familyId: number) =>
    request<FamilyPropertiesResponse>(`/families/${familyId}/properties`),

  // Pruefwarteschlange der Merkmalswerte: alle Werte quer ueber die Artikel.
  // Reiner Lesepfad -- bestaetigt wird ueber `updateProperty`, damit es nur
  // EINEN Schreibweg mit EINER Rechtepruefung gibt.
  propertyReview: (q: PropertyReviewQuery) =>
    request<PropertyReviewResponse>(`/properties/review${toReviewQuery(q)}`),
  createProperty: (input: CreatePropertyRequest) =>
    request<{ items: ArticlePropertiesResponse["items"] }>("/properties", { method: "POST", body: JSON.stringify(input) }),
  updateProperty: (id: number, input: UpdatePropertyRequest) =>
    request<{ items: ArticlePropertiesResponse["items"] }>(`/properties/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteProperty: (id: number) =>
    request<{ deleted: boolean }>(`/properties/${id}`, { method: "DELETE" }),
  createText: (input: CreateTextRequest) =>
    request<ProductText>("/texts", { method: "POST", body: JSON.stringify(input) }),
  createLocale: (textId: number, input: CreateLocaleRequest) =>
    request<ProductText>(`/texts/${textId}/locales`, { method: "POST", body: JSON.stringify(input) }),
  updateLocale: (localeId: number, input: UpdateLocaleRequest) =>
    request<ProductText>(`/text-locales/${localeId}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteLocale: (localeId: number) =>
    request<{ textDeleted: boolean }>(`/text-locales/${localeId}`, { method: "DELETE" }),

  // Produktmodell- und Variantenebene. Lesen mit article.read, schreiben mit
  // model.write, DTE-Listung mit dte.listing -- geprueft wird im Server.
  models: (q: { q?: string; familyId?: number; categoryId?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set("q", q.q);
    if (q.familyId) p.set("familyId", String(q.familyId));
    if (q.categoryId) p.set("categoryId", String(q.categoryId));
    const s = p.toString();
    return request<ProductModelListResponse>(`/models${s ? `?${s}` : ""}`);
  },
  model: (id: number) => request<ProductModelDetail>(`/models/${id}`),
  modelProperties: (id: number) => request<ModelPropertiesResponse>(`/models/${id}/properties`),
  modelTexts: (id: number) => request<{ modelId: number; items: readonly ProductText[] }>(`/models/${id}/texts`),
  createModel: (input: CreateModelRequest) =>
    request<ProductModelDetail>("/models", { method: "POST", body: JSON.stringify(input) }),
  updateModel: (id: number, input: UpdateModelRequest) =>
    request<ProductModelDetail>(`/models/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteModel: (id: number) =>
    request<{ deleted: boolean }>(`/models/${id}`, { method: "DELETE" }),
  /* Die Modellseite V2 (Produktmodelle 002). */
  modelOverview: (id: number) => request<ModelOverviewResponse>(`/models/${id}/overview`),
  modelCommonData: (id: number) => request<ModelCommonDataResponse>(`/models/${id}/common-data`),
  adoptCommonValue: (id: number, input: AdoptCommonValueRequest) =>
    request<ModelCommonDataResponse>(`/models/${id}/common-data/adopt`,
      { method: "POST", body: JSON.stringify(input) }),
  modelHistory: (id: number) => request<ModelHistoryResponse>(`/models/${id}/history`),
  /* Produktmodelle 002A: Artikel einer Ausfuehrung zuordnen, Modell sicher loeschen. */
  executionCandidates: (variantId: number, q: string) =>
    request<AssignmentCandidatesResponse>(`/model-variants/${variantId}/candidates?q=${encodeURIComponent(q)}`),
  assignArticleToExecution: (variantId: number, input: AssignArticleRequest) =>
    request<AssignmentCheck>(`/model-variants/${variantId}/assign-article`,
      { method: "POST", body: JSON.stringify(input) }),
  modelDeletePreview: (id: number) => request<ModelDeletePreview>(`/models/${id}/delete-preview`),
  /* Produktmodelle 002B: Zuordnung loesen, Fehlanlage verwerfen. */
  unassignPreview: (variantId: number) =>
    request<UnassignPreview>(`/model-variants/${variantId}/unassign-preview`),
  unassignExecution: (variantId: number) =>
    request<UnassignPreview>(`/model-variants/${variantId}/unassign-article`, { method: "POST" }),
  discardPreview: (articleId: number) => request<DiscardPreview>(`/articles/${articleId}/discard`),
  discardFailedCreation: (articleId: number, input: DiscardRequest) =>
    request<{ articleNumber: string; tombstoneId: number; model: string | null }>(
      `/articles/${articleId}/discard`, { method: "POST", body: JSON.stringify(input) }),
  addDimension: (modelId: number, input: CreateDimensionRequest) =>
    request<ProductModelDetail>(`/models/${modelId}/dimensions`, { method: "POST", body: JSON.stringify(input) }),
  deleteDimension: (id: number) =>
    request<ProductModelDetail>(`/model-dimensions/${id}`, { method: "DELETE" }),
  addDimensionValue: (dimensionId: number, input: CreateDimensionValueRequest) =>
    request<ProductModelDetail>(`/model-dimensions/${dimensionId}/values`, { method: "POST", body: JSON.stringify(input) }),
  deleteDimensionValue: (id: number) =>
    request<ProductModelDetail>(`/model-dimension-values/${id}`, { method: "DELETE" }),
  createVariant: (modelId: number, input: CreateVariantRequest) =>
    request<ProductModelDetail>(`/models/${modelId}/variants`, { method: "POST", body: JSON.stringify(input) }),
  updateVariant: (id: number, input: UpdateVariantRequest) =>
    request<ProductModelDetail>(`/model-variants/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteVariant: (id: number) =>
    request<ProductModelDetail>(`/model-variants/${id}`, { method: "DELETE" }),
  assignArticleModel: (articleId: number, modelId: number | null) =>
    request<ArticleDetail>(`/articles/${articleId}/model`, { method: "PUT", body: JSON.stringify({ modelId }) }),
  setDteListing: (articleId: number, input: SetDteListingRequest) =>
    request<ArticleDetail>(`/articles/${articleId}/dte-listing`, { method: "PUT", body: JSON.stringify(input) }),

  // Artikelstamm-Bereinigung. Beide Leseaufrufe verlangen serverseitig
  // `property.review.read` -- die Ansicht zeigt Befunde und Quellwortlaute.
  // Textentwurf -- der Server bildet ihn und speichert ihn NICHT.
  textDraft: (articleId: number, type: string, language: string) =>
    request<{ textType: string; language: string; quelle: string; text: string;
              fehlend: readonly string[]; method: string }>(
      `/articles/${articleId}/text-draft?type=${encodeURIComponent(type)}` +
      `&language=${encodeURIComponent(language)}`),

  // Nachgewiesene Eigenschaften -- Katalog und Nachweise am Artikel.
  capabilityCatalog: () => request<{ items: readonly ProvenCapabilityCatalog[] }>(`/capabilities`),
  articleCapabilities: (articleId: number) =>
    request<{ items: readonly ArticleCapability[] }>(`/articles/${articleId}/capabilities`),
  addCapabilityEvidence: (articleId: number, standardId: number) =>
    request<{ items: readonly ArticleCapability[] }>(`/articles/${articleId}/capabilities`,
      { method: "POST", body: JSON.stringify({ standardId }) }),
  setEvidenceQuality: (evidenceId: number, quality: string) =>
    request<{ ok: true }>(`/capability-evidence/${evidenceId}`,
      { method: "PATCH", body: JSON.stringify({ quality }) }),
  removeCapabilityEvidence: (evidenceId: number) =>
    request<{ deleted: true }>(`/capability-evidence/${evidenceId}`, { method: "DELETE" }),

  /* ── Produktdokumente ───────────────────────────────────────────────────
   *
   * Lesen mit `article.read`, schreiben mit `article.write` -- geprueft wird
   * im Server.
   *
   * `documentUrl` ist die eine Ausnahme von „keine Komponente kennt einen
   * API-Pfad": eine Datei wird nicht geholt und dann angezeigt, sondern der
   * Browser laedt sie selbst ueber ein `<a href>`. Damit trotzdem kein
   * zweiter Ort entsteht, an dem ein Pfad steht, wird er hier gebaut.
   */
  articleDocuments: (articleId: number) =>
    request<ArticleDocumentsResponse>(`/articles/${articleId}/documents`),
  modelDocuments: (modelId: number) =>
    request<ModelDocumentsResponse>(`/models/${modelId}/documents`),
  uploadArticleDocument: (articleId: number, input: UploadDocumentRequest) =>
    request<ArticleDocumentsResponse>(`/articles/${articleId}/documents`,
      { method: "POST", body: JSON.stringify(input) }),
  uploadModelDocument: (modelId: number, input: UploadDocumentRequest) =>
    request<ModelDocumentsResponse>(`/models/${modelId}/documents`,
      { method: "POST", body: JSON.stringify(input) }),
  removeDocument: (documentId: number) =>
    request<{ removed: true }>(`/documents/${documentId}`, { method: "DELETE" }),
  /**
   * Die Adresse der Datei.
   *
   * `inline` ist eine BITTE, keine Anweisung: der Server liefert nur an,
   * was er fuer anzeigbar haelt, und schickt alles andere als Download.
   * Die Entscheidung gehoert dorthin -- eine Oberflaeche, die sie faellte,
   * waere die zweite Stelle, an der sie falsch fallen kann.
   */
  documentUrl: (documentId: number, inline: boolean) =>
    `${API_BASE}/documents/${documentId}/content${inline ? "?disposition=inline" : ""}`,

  cleanupFamilies: () => request<CleanupFamilyResponse>(`/cleanup/families`),
  cleanupModel: (modelId: number) => request<CleanupModelView>(`/cleanup/models/${modelId}`),
  openReviewFlag: (articleId: number, input: OpenReviewFlagRequest) =>
    request<{ ok: true }>(`/articles/${articleId}/review-flags`,
      { method: "POST", body: JSON.stringify(input) }),
  resolveReviewFlag: (flagId: number, input: ResolveReviewFlagRequest) =>
    request<{ ok: true }>(`/review-flags/${flagId}`,
      { method: "PATCH", body: JSON.stringify(input) }),

  // Logistik: Supplier-Produkt-Beziehung und Verpackung. Lesen mit
  // article.read, schreiben mit logistics.write, fachlich bestaetigen mit
  // logistics.confirm -- geprueft wird im Server, nicht hier.
  //
  // Es gibt hier bewusst KEINEN Aufruf, der ein Verpackungsvolumen setzt:
  // CBM wird berechnet und besitzt keinen Schreibweg.
  articleLogistics: (articleId: number) =>
    request<ArticleLogisticsResponse>(`/articles/${articleId}/logistics`),
  setTariffCode: (articleId: number, input: SetTariffCodeRequest) =>
    request<ArticleLogisticsResponse>(`/articles/${articleId}/tariff-code`,
      { method: "PUT", body: JSON.stringify(input) }),
  /*
   * Zollstammdaten (0080). Gelesen wird je Artikel; geschrieben wird dort,
   * wo die Angabe gilt -- am Artikel der Vorgabewert, an der Beziehung die
   * Abweichung. Jeder Schreibweg gibt die ganze Artikelsicht zurueck, weil
   * eine Abweichung erst im Verhaeltnis zum Vorgabewert eine Aussage ist.
   */
  articleCustoms: (articleId: number) =>
    request<ArticleCustomsResponse>(`/articles/${articleId}/customs-facts`),
  setArticleCustomsFact: (articleId: number, fact: CustomsFactCode, input: CustomsFactRequest) =>
    request<ArticleCustomsResponse>(`/articles/${articleId}/customs-facts/${fact}`,
      { method: "PUT", body: JSON.stringify(input) }),
  removeArticleCustomsFact: (articleId: number, fact: CustomsFactCode) =>
    request<ArticleCustomsResponse>(`/articles/${articleId}/customs-facts/${fact}`,
      { method: "DELETE" }),
  setRelationCustomsFact: (relationId: number, fact: CustomsFactCode, input: CustomsFactRequest) =>
    request<ArticleCustomsResponse>(`/supplier-articles/${relationId}/customs-facts/${fact}`,
      { method: "PUT", body: JSON.stringify(input) }),
  removeRelationCustomsFact: (relationId: number, fact: CustomsFactCode) =>
    request<ArticleCustomsResponse>(`/supplier-articles/${relationId}/customs-facts/${fact}`,
      { method: "DELETE" }),
  createSupplierArticle: (input: CreateSupplierArticleRequest) =>
    request<SupplierArticle>("/supplier-articles", { method: "POST", body: JSON.stringify(input) }),
  updateSupplierArticle: (id: number, input: UpdateSupplierArticleRequest) =>
    request<SupplierArticle>(`/supplier-articles/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteSupplierArticle: (id: number) =>
    request<{ deleted: boolean }>(`/supplier-articles/${id}`, { method: "DELETE" }),
  /* Logistik am Produktmodell (0057): was die SKUs ohne eigene Angabe erben. */
  modelLogistics: (modelId: number) =>
    request<ModelPackagingResponse>(`/models/${modelId}/packaging`),
  setModelTariffCode: (modelId: number, input: SetTariffCodeRequest) =>
    request<ModelPackagingResponse>(`/models/${modelId}/tariff-code`,
      { method: "PUT", body: JSON.stringify(input) }),
  createModelPackagingVersion: (modelId: number, input: CreatePackagingVersionRequest) =>
    request<ModelPackagingResponse>(`/models/${modelId}/packaging`,
      { method: "POST", body: JSON.stringify(input) }),
  createPackagingVersion: (relationId: number, input: CreatePackagingVersionRequest) =>
    request<SupplierArticle>(`/supplier-articles/${relationId}/packaging`,
      { method: "POST", body: JSON.stringify(input) }),
  updatePackagingVersion: (id: number, input: UpdatePackagingVersionRequest) =>
    request<SupplierArticle>(`/packaging-versions/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deletePackagingVersion: (id: number) =>
    request<SupplierArticle>(`/packaging-versions/${id}`, { method: "DELETE" }),
  addPackagingLevel: (versionId: number, input: CreatePackagingLevelRequest) =>
    request<SupplierArticle>(`/packaging-versions/${versionId}/levels`,
      { method: "POST", body: JSON.stringify(input) }),
  updatePackagingLevel: (id: number, input: UpdatePackagingLevelRequest) =>
    request<SupplierArticle>(`/packaging-levels/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deletePackagingLevel: (id: number) =>
    request<SupplierArticle>(`/packaging-levels/${id}`, { method: "DELETE" }),
  setPackagingMaterials: (levelId: number, input: SetPackagingMaterialsRequest) =>
    request<SupplierArticle>(`/packaging-levels/${levelId}/materials`,
      { method: "PUT", body: JSON.stringify(input) }),
  addPackagingTest: (versionId: number, input: CreatePackagingTestRequest) =>
    request<SupplierArticle>(`/packaging-versions/${versionId}/tests`,
      { method: "POST", body: JSON.stringify(input) }),
  deletePackagingTest: (id: number) =>
    request<SupplierArticle>(`/packaging-tests/${id}`, { method: "DELETE" }),

  /* --- Produktakte: Anlage, Grunddaten, Beiwerk ------------------------- */
  //
  // Die Akte ist EIN Aufruf, aber KEINE Kopie: jeder Eintrag traegt die
  // Kennung seines echten Datensatzes, und geaendert wird ueber denselben
  // Weg, den auch die Fachansicht benutzt.
  /* Eine neue Produktfamilie -- aus dem Anlageprozess heraus, ohne ihn zu
     verlassen. Die Aehnlichkeitswarnung wird davor gelesen, nicht danach. */
  /* Der Lebenszyklus der Strukturen -- dieselben sechs Wege fuer
     Hauptkategorie und Produktfamilie (Lifecycle 001). */
  structurePreview: (kind: StructureKind, id: number) =>
    request<StructureDeletePreview>(`/${kind === "category" ? "categories" : "families"}/${id}/delete-preview`),
  updateStructure: (kind: StructureKind, id: number, input: UpdateStructureRequest) =>
    request<StructureDeletePreview>(`/${kind === "category" ? "categories" : "families"}/${id}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  archiveStructure: (kind: StructureKind, id: number, note?: string | null) =>
    request<StructureDeletePreview>(`/${kind === "category" ? "categories" : "families"}/${id}/archive`,
      { method: "POST", body: JSON.stringify({ note: note ?? null }) }),
  reactivateStructure: (kind: StructureKind, id: number) =>
    request<StructureDeletePreview>(`/${kind === "category" ? "categories" : "families"}/${id}/reactivate`,
      { method: "POST", body: "{}" }),
  trashStructure: (kind: StructureKind, id: number, note?: string | null) =>
    request<StructureDeletePreview>(`/${kind === "category" ? "categories" : "families"}/${id}/trash`,
      { method: "POST", body: JSON.stringify({ note: note ?? null }) }),
  restoreStructure: (kind: StructureKind, id: number) =>
    request<StructureDeletePreview>(`/${kind === "category" ? "categories" : "families"}/${id}/restore`,
      { method: "POST", body: "{}" }),
  purgeStructure: (kind: StructureKind, id: number, confirmCode: string) =>
    request<{ deleted: boolean; code: string; name: string }>(
      `/${kind === "category" ? "categories" : "families"}/${id}`,
      { method: "DELETE", body: JSON.stringify({ confirmCode }) }),
  removeCommonValue: (modelId: number, input: RemoveCommonValueRequest) =>
    request<ModelCommonDataResponse>(`/models/${modelId}/common-data/remove`,
      { method: "POST", body: JSON.stringify(input) }),

  /** Die erwarteten Informationen eines Artikels samt Zustand (Produktdaten 001 A). */
  articleRequirements: (articleId: number) =>
    request<ArticleRequirements>(`/articles/${articleId}/requirements`),
  modelRequirements: (modelId: number) =>
    request<ModelRequirements>(`/models/${modelId}/requirements`),

  /** Was jede Ausfuehrung dabei abgibt -- und ob sie darf (PM003-FACH-02). */
  dropOwnPreview: (modelId: number, definition: string) =>
    request<DropOwnPreview>(
      `/models/${modelId}/common-data/drop-own-preview?definition=${encodeURIComponent(definition)}`),

  /** Den doppelt gepflegten eigenen Wert einer Ausfuehrung abgeben (PM003-FACH-01). */
  dropOwnValues: (modelId: number, input: DropOwnValueRequest) =>
    request<ModelCommonDataResponse>(`/models/${modelId}/common-data/drop-own`,
      { method: "POST", body: JSON.stringify(input) }),

  /**
   * Einen Befund KLAEREN (Produktmodelle 003 V2).
   *
   * Der Endpunkt gibt es seit 0051; eine Bedienung dafuer gab es nicht --
   * die Warnung liess sich nur durch eine Wertaenderung loswerden. Die
   * Begruendung ist Pflicht, und seit 0062 gilt die Klaerung fuer genau
   * den Quellstand, ueber den entschieden wurde.
   */
  clarifyFinding: (propertyId: number, findingCode: string, note: string) =>
    request<{ clarified: boolean }>(`/properties/${propertyId}/clarifications`,
      { method: "POST", body: JSON.stringify({ findingCode, note }) }),

  /* Merkmalskatalog (Produktmodelle 003). */
  propertyDefinitionUsage: (code: string) =>
    request<PropertyDefinitionUsage>(`/property-definitions/${encodeURIComponent(code)}`),
  similarDefinitions: (code: string, name: string) =>
    request<{ items: readonly { code: string; nameDe: string | null; group: string; valueType: string; inUse: boolean }[] }>(
      `/property-definitions/similar?code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}`),
  createPropertyDefinition: (input: CreatePropertyDefinitionRequest) =>
    request<PropertyDefinitionUsage>("/property-definitions", { method: "POST", body: JSON.stringify(input) }),
  updatePropertyDefinition: (code: string, input: UpdatePropertyDefinitionRequest) =>
    request<PropertyDefinitionUsage>(`/property-definitions/${encodeURIComponent(code)}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  deletePropertyDefinition: (code: string, confirmCode: string) =>
    request<{ deleted: boolean; code: string }>(`/property-definitions/${encodeURIComponent(code)}`,
      { method: "DELETE", body: JSON.stringify({ confirmCode }) }),

  /* Altartikel-Uebernahme (Produktmodelle 003). */
  adoptPreview: (modelId: number, articleId: number) =>
    request<AdoptFromArticlePreview>(`/models/${modelId}/adopt-preview?articleId=${articleId}`),
  adoptFromArticle: (modelId: number, input: AdoptFromArticleRequest) =>
    request<{ adopted: readonly string[]; skipped: readonly string[] }>(
      `/models/${modelId}/adopt-from-article`, { method: "POST", body: JSON.stringify(input) }),

  createCategory: (input: CreateCategoryRequest) =>
    request<CategoryItem>("/categories", { method: "POST", body: JSON.stringify(input) }),
  similarCategories: (code: string, name: string) =>
    request<{ items: readonly CategoryItem[] }>(
      `/categories/similar?code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}`),
  createFamily: (input: CreateFamilyRequest) =>
    request<FamilyItem>("/families", { method: "POST", body: JSON.stringify(input) }),
  similarFamilies: (categoryId: number, code: string, name: string) =>
    request<{ items: readonly FamilyItem[] }>(
      `/families/similar?categoryId=${categoryId}`
      + `&code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}`),
  createArticle: (input: CreateArticleRequest) =>
    request<{ id: number }>("/articles", { method: "POST", body: JSON.stringify(input) }),
  updateArticle: (id: number, input: UpdateArticleRequest) =>
    request<ArticleDetail>(`/articles/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  articleDossier: (id: number) => request<ArticleDossier>(`/articles/${id}/dossier`),
  createRelation: (articleId: number, input: {
    targetArticleId: number; relationType: string; note?: string | null;
  }) => request<ArticleRelationRef>(`/articles/${articleId}/relations`,
    { method: "POST", body: JSON.stringify(input) }),
  deleteRelation: (id: number) =>
    request<{ deleted: boolean }>(`/article-relations/${id}`, { method: "DELETE" }),
  /* Die Bewertung haengt am LIEFERANTEN -- eigene Adresse, nicht die der
     Beziehung. Sie gilt fuer jeden Artikel dieses Partners. */
  setSupplierRating: (supplierId: number, input: UpdateSupplierRatingRequest) =>
    request<SupplierRating>(`/suppliers/${supplierId}/rating`,
      { method: "PATCH", body: JSON.stringify(input) }),
  /* Der Plan wird als GANZES gesetzt -- es gibt keinen Weg, eine einzelne
     Stufe zu aendern, und das ist Absicht. */
  setPaymentTerms: (ebene: "suppliers" | "supplier-articles", id: number,
                    input: SetPaymentTermsRequest) =>
    request<PaymentTerms>(`/${ebene}/${id}/payment-terms`,
      { method: "PUT", body: JSON.stringify(input) }),
  /* ── Das Lieferantenportal ───────────────────────────────────────── */
  /* Ohne Rolle: alle Geschaeftspartner. Mit Rolle: nur die, die sie tragen. */
  suppliers: (rolle?: string) => request<{ items: SupplierListItem[] }>(
    rolle === undefined ? "/suppliers" : `/suppliers?role=${encodeURIComponent(rolle)}`),
  supplier: (id: number) => request<SupplierDetail>(`/suppliers/${id}`),
  createSupplier: (input: CreateSupplierRequest) =>
    request<SupplierDetail>("/suppliers", { method: "POST", body: JSON.stringify(input) }),
  /* Der Datenbedarf wird GERECHNET, nicht gelesen -- jeder Aufruf gibt den
     heutigen Stand. Deshalb kein Zwischenspeicher. */
  supplierRequirements: (id: number) =>
    request<SupplierRequirements>(`/suppliers/${id}/requirements`),
  supplierArticles: (id: number) =>
    request<{ items: SupplierArticleRow[] }>(`/suppliers/${id}/articles`),
  updateSupplier: (id: number, input: UpdateSupplierRequest) =>
    request<SupplierDetail>(`/suppliers/${id}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  createSupplierContact: (id: number, input: UpsertSupplierContactRequest) =>
    request<{ items: SupplierContact[] }>(`/suppliers/${id}/contacts`,
      { method: "POST", body: JSON.stringify(input) }),
  updateSupplierContact: (id: number, contactId: number, input: UpsertSupplierContactRequest) =>
    request<{ items: SupplierContact[] }>(`/suppliers/${id}/contacts/${contactId}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  createSupplierNote: (id: number, input: UpsertSupplierNoteRequest) =>
    request<{ items: SupplierNote[] }>(`/suppliers/${id}/notes`,
      { method: "POST", body: JSON.stringify(input) }),
  updateSupplierNote: (id: number, noteId: number, input: UpsertSupplierNoteRequest) =>
    request<{ items: SupplierNote[] }>(`/suppliers/${id}/notes/${noteId}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  deleteSupplierNote: (id: number, noteId: number) =>
    request<{ items: SupplierNote[] }>(`/suppliers/${id}/notes/${noteId}`,
      { method: "DELETE" }),
  createSupplierBankAccount: (id: number, input: UpsertSupplierBankAccountRequest) =>
    request<{ items: SupplierBankAccount[] }>(`/suppliers/${id}/bank-accounts`,
      { method: "POST", body: JSON.stringify(input) }),
  updateSupplierBankAccount: (
    id: number, accountId: number, input: UpsertSupplierBankAccountRequest,
  ) =>
    request<{ items: SupplierBankAccount[] }>(`/suppliers/${id}/bank-accounts/${accountId}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  deleteSupplierBankAccount: (id: number, accountId: number) =>
    request<{ items: SupplierBankAccount[] }>(`/suppliers/${id}/bank-accounts/${accountId}`,
      { method: "DELETE" }),
  /* ── Produktions- und Logistikportal ───────────────────────────── */
  timeline: () => request<TimelineResponse>("/procurement/timeline"),
  purchaseOrders: () => request<{ items: PurchaseOrderListItem[] }>("/purchase-orders"),
  purchaseOrder: (id: number) => request<PurchaseOrderDetail>(`/purchase-orders/${id}`),
  /* ── Die kaufmaennische Sicht ──────────────────────────────────────── */
  purchaseOrderFinance: (id: number) =>
    request<PurchaseOrderFinance>(`/purchase-orders/${id}/finance`),
  addPayment: (id: number, input: UpsertPaymentRequest) =>
    request<PurchaseOrderFinance>(`/purchase-orders/${id}/payments`,
      { method: "POST", body: JSON.stringify(input) }),
  updatePayment: (id: number, paymentId: number, input: UpsertPaymentRequest) =>
    request<PurchaseOrderFinance>(`/purchase-orders/${id}/payments/${paymentId}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  deletePayment: (id: number, paymentId: number) =>
    request<PurchaseOrderFinance>(`/purchase-orders/${id}/payments/${paymentId}`,
      { method: "DELETE" }),
  /* Nur lesend -- der Bericht ist eine Sicht und kein eigener Bestand. */
  commitments: (asOf: string) =>
    request<CommitmentReport>(`/procurement/commitments?asOf=${encodeURIComponent(asOf)}`),

  /* ── Die Erfassung aus einem Beleg ─────────────────────────────────── */
  captures: () => request<{ items: PurchaseOrderCapture[] }>("/purchase-orders/captures"),
  capture: (id: number) => request<PurchaseOrderCapture>(`/purchase-orders/captures/${id}`),
  createCapture: (input: CreateCaptureRequest) =>
    request<PurchaseOrderCapture>("/purchase-orders/captures",
      { method: "POST", body: JSON.stringify(input) }),
  confirmCapture: (id: number, input: ConfirmCaptureRequest) =>
    request<PurchaseOrderDetail>(`/purchase-orders/captures/${id}/confirm`,
      { method: "POST", body: JSON.stringify(input) }),
  discardCapture: (id: number) =>
    request<{ ok: boolean }>(`/purchase-orders/captures/${id}`, { method: "DELETE" }),
  createPurchaseOrder: (input: CreatePurchaseOrderRequest) =>
    request<PurchaseOrderDetail>("/purchase-orders",
      { method: "POST", body: JSON.stringify(input) }),
  updatePurchaseOrder: (id: number, input: UpdatePurchaseOrderRequest) =>
    request<PurchaseOrderDetail>(`/purchase-orders/${id}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  deletePurchaseOrder: (id: number) =>
    request<{ ok: boolean }>(`/purchase-orders/${id}`, { method: "DELETE" }),
  addPurchaseOrderLine: (id: number, input: UpsertPurchaseOrderLineRequest) =>
    request<PurchaseOrderDetail>(`/purchase-orders/${id}/lines`,
      { method: "POST", body: JSON.stringify(input) }),
  updatePurchaseOrderLine: (
    id: number, lineId: number, input: UpsertPurchaseOrderLineRequest,
  ) =>
    request<PurchaseOrderDetail>(`/purchase-orders/${id}/lines/${lineId}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  deletePurchaseOrderLine: (id: number, lineId: number) =>
    request<PurchaseOrderDetail>(`/purchase-orders/${id}/lines/${lineId}`,
      { method: "DELETE" }),
  consolidations: () => request<{ items: ConsolidationListItem[] }>("/consolidations"),
  consolidation: (id: number) => request<ConsolidationDetail>(`/consolidations/${id}`),
  /* Was mit dieser Lieferung ankommt -- ohne die ganze Akte zu laden. */
  consolidationGoods: (id: number) =>
    request<ConsolidationGoods>(`/consolidations/${id}/goods`),
  /* Die Dokumente einer Sendung -- referenziert, nie kopiert. */
  consolidationDocuments: (id: number) =>
    request<ConsolidationDocumentsResponse>(`/consolidations/${id}/documents`),
  /* Die drei Faecher einer Bestellung -- immer alle drei, auch die leeren. */
  orderDocuments: (id: number) =>
    request<OrderDocumentsResponse>(`/purchase-orders/${id}/documents`),
  /* Ein Zahlungsnachweis -- an DIESE Zahlung, nicht lose an die Bestellung. */
  uploadPaymentEvidence: (poId: number, paymentId: number,
                          body: UploadPaymentEvidenceRequest) =>
    request<PurchaseOrderDetail>(
      `/purchase-orders/${poId}/payments/${paymentId}/evidence`,
      { method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify(body) }),
  uploadOrderDocument: (id: number, body: UploadOrderDocumentRequest) =>
    request<OrderDocumentsResponse>(`/purchase-orders/${id}/documents`,
      { method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify(body) }),
  createConsolidation: (input: CreateConsolidationRequest) =>
    request<ConsolidationDetail>("/consolidations",
      { method: "POST", body: JSON.stringify(input) }),
  updateConsolidation: (id: number, input: UpdateConsolidationRequest) =>
    request<ConsolidationDetail>(`/consolidations/${id}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  assignOrder: (cnId: number, poId: number) =>
    request<ConsolidationDetail>(`/consolidations/${cnId}/orders/${poId}`, { method: "PUT" }),
  /* Loesen, nicht loeschen: die Zuordnung bleibt stehen und bekommt einen
     Grund. Deshalb POST auf eine eigene Adresse und kein DELETE. */
  /* Eingang beim Logistikpartner -- eine Tatsache ueber DIESE Bestellung in
     DIESER Sendung, und kein Transportbeginn. */
  setForwarderReceipt: (cnId: number, poId: number, input: ForwarderReceiptRequest) =>
    request<ConsolidationDetail>(`/consolidations/${cnId}/orders/${poId}/forwarder-receipt`,
      { method: "PUT", body: JSON.stringify(input) }),
  releaseOrder: (cnId: number, poId: number, input: ReleaseAssignmentRequest) =>
    request<ConsolidationDetail>(`/consolidations/${cnId}/orders/${poId}/release`,
      { method: "POST", body: JSON.stringify(input) }),
  setMilestone: (input: UpsertMilestoneRequest) =>
    request<{ items: Milestone[] }>("/procurement/milestones",
      { method: "POST", body: JSON.stringify(input) }),
  deleteMilestone: (id: number) =>
    request<{ ok: boolean }>(`/procurement/milestones/${id}`, { method: "DELETE" }),
  leadTimeRules: () => request<{ items: LeadTimeRule[] }>("/procurement/lead-time-rules"),
  setLeadTimeRule: (input: UpsertLeadTimeRuleRequest) =>
    request<{ items: LeadTimeRule[] }>("/procurement/lead-time-rules",
      { method: "POST", body: JSON.stringify(input) }),
  deleteLeadTimeRule: (id: number) =>
    request<{ items: LeadTimeRule[] }>(`/procurement/lead-time-rules/${id}`,
      { method: "DELETE" }),

  /* Die China-Planungsebene. Die LISTE zeigt auch die stillgelegten --
     die Zeitachse zeigt nur die aktiven und bekommt sie mit ihrer
     eigenen Antwort. */
  chinaEvents: () =>
    request<ChinaEventsResponse>("/procurement/china-events"),
  createChinaEvent: (input: UpsertChinaEventRequest) =>
    request<ChinaEvent>("/procurement/china-events",
      { method: "POST", body: JSON.stringify(input) }),
  updateChinaEvent: (id: number, input: UpsertChinaEventRequest) =>
    request<ChinaEvent>(`/procurement/china-events/${id}`,
      { method: "PATCH", body: JSON.stringify(input) }),
  deactivateChinaEvent: (id: number) =>
    request<{ ok: boolean }>(`/procurement/china-events/${id}`, { method: "DELETE" }),

  deleteSupplierContact: (id: number, contactId: number) =>
    request<{ items: SupplierContact[] }>(`/suppliers/${id}/contacts/${contactId}`,
      { method: "DELETE" }),

  confirmPaymentTerms: (ebene: "suppliers" | "supplier-articles", id: number) =>
    request<PaymentTerms>(`/${ebene}/${id}/payment-terms`, { method: "PATCH" }),
  removePaymentTerms: (ebene: "suppliers" | "supplier-articles", id: number) =>
    request<{ deleted: boolean }>(`/${ebene}/${id}/payment-terms`, { method: "DELETE" }),
  createBreakerCapacity: (articleId: number, input: {
    characteristic: string; ratedCurrent: number; maxFixtures: number;
    relationId?: number | null; note?: string | null;
  }) => request<BreakerCapacity>(`/articles/${articleId}/breaker-capacities`,
    { method: "POST", body: JSON.stringify(input) }),
  deleteBreakerCapacity: (id: number) =>
    request<{ deleted: boolean }>(`/breaker-capacities/${id}`, { method: "DELETE" }),
  setInformationRequest: (articleId: number, input: {
    definition: string; status: string; responsible?: string | null; note?: string | null;
  }) => request<InformationRequestRef>(`/articles/${articleId}/information-requests`,
    { method: "PUT", body: JSON.stringify(input) }),
  deleteInformationRequest: (id: number) =>
    request<{ deleted: boolean }>(`/information-requests/${id}`, { method: "DELETE" }),

  /* --- Kalkulation ------------------------------------------------------ */
  //
  // Vertraulich wie der Einkauf: `purchasing.read` zum Lesen, eigene Rechte
  // zum Schreiben. Geprueft wird im Server -- diese Datei kennt keine Regel.
  calculationParameters: () =>
    request<CalculationParametersResponse>("/calculation-parameters"),
  setCalculationParameter: (input: SetCalculationParameterRequest) =>
    request<{ setId: number }>("/calculation-parameters",
      { method: "PUT", body: JSON.stringify(input) }),
  createParameterSet: (input: { validFrom: string; values: readonly { parameterCode: string; value: number }[] }) =>
    request<{ setId: number; validFrom: string; revision: number; created: boolean }>("/calculation-parameter-sets",
      { method: "POST", body: JSON.stringify(input) }),
  setFamilyOverride: (input: { familyId: number; parameterCode: string; value: number }) =>
    request<{ familyId: number; parameterCode: string; value: string }>("/calculation-parameters/family-overrides",
      { method: "PUT", body: JSON.stringify(input) }),
  setDutyRate: (input: SetDutyRateRequest) =>
    request<{ id: number }>("/customs-duty-rates", { method: "PUT", body: JSON.stringify(input) }),
  setSupplierPrice: (relationId: number, input: SetSupplierPriceRequest) =>
    request<{ id: number }>(`/supplier-articles/${relationId}/prices`,
      { method: "PUT", body: JSON.stringify(input) }),
  articleCalculation: (articleId: number) =>
    request<ArticleCalculationResponse>(`/articles/${articleId}/calculation`),
  calculate: (articleId: number, input: CalculateRequest = {}) =>
    request<{ id: number }>(`/articles/${articleId}/calculation`,
      { method: "POST", body: JSON.stringify(input) }),
  releasePrice: (articleId: number, input: ReleasePriceRequest) =>
    request<{ id?: number; ids?: number[] }>(`/articles/${articleId}/price-releases`,
      { method: "POST", body: JSON.stringify(input) }),
  /* Der Transportvergleich -- gerechnet, nie gespeichert, nie freigebbar. */
  calculationScenarios: (articleId: number) =>
    request<CalculationScenariosResponse>(`/articles/${articleId}/calculation/scenarios`),
  /* Die Varianten des Produktmodells mit ihrem Preisstand -- fuer die Modellfreigabe. */
  modelReleaseScope: (articleId: number) =>
    request<ModelReleaseScope | null>(`/articles/${articleId}/model-release-scope`),

  users: () => request<UserListResponse>("/users"),
  createUser: (input: CreateUserRequest) =>
    request<UserListItem>("/users", { method: "POST", body: JSON.stringify(input) }),
  updateUser: (id: number, input: UpdateUserRequest) =>
    request<UserListItem>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
};
